import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@ember-data/model';
import { action } from '@ember/object';
import type { Type } from '@warp-drive/core-types/symbols';
import type MeasurementModel from './measurement';
import type OwnerModel from './owner';
import type TargetModel from './target';

export default class MetricModel extends Model {
  declare [Type]: 'metric';
  @attr('string') declare title: string;
  @attr declare description: unknown;
  @attr('number', { defaultValue: 2 }) declare decimals: number;
  @attr('string') declare symbol: string;
  @attr('string') declare trend: string;
  @attr('string') declare reporting_period: string;
  @attr('boolean') declare active: boolean;
  @belongsTo('owner', { async: true, polymorphic: true, inverse: 'metrics' })
  declare owner: AsyncBelongsTo<OwnerModel>;
  @hasMany('target', {
    async: true,
    inverse: 'metric',
  })
  declare targets: AsyncHasMany<TargetModel>;
  @hasMany('measurement', {
    async: true,
    inverse: 'metric',
  })
  declare measurements: AsyncHasMany<MeasurementModel>;

  get latestMeasurement() {
    const measurements = this.measurements.content;
    if (!measurements?.length) return undefined;
    const savedMeasurements = measurements.filter((m) => m.id);
    const latest = savedMeasurements[savedMeasurements.length - 1];
    return latest;
  }

  @action
  targetForInitiative(initiative_id: string) {
    const targets = this.targets.content ?? [];
    const target = targets.find((_target) => {
      return _target.initiative?.content?.id === initiative_id;
    });
    return target;
  }

  @action
  targetForGoal(goal_id: string) {
    const targets = this.targets.content ?? [];
    const target = targets.find((_target) => {
      return _target.goal?.content?.id === goal_id;
    });
    return target;
  }

  get linkedInitiatives() {
    const targets = this.targets.content ?? [];
    const initiative_targets = targets.filter(
      (item) => item.initiative.content,
    );
    return initiative_targets;
  }

  get linkedGoals() {
    const targets = this.targets.content ?? [];
    const goal_targets = targets.filter((item) => item.goal.content);
    return goal_targets;
  }

  get changeset_object() {
    return {
      title: this.title,
      description: this.description,
      decimals: this.decimals,
      symbol: this.symbol,
      trend: this.trend,
      reporting_period: this.reporting_period,
      active: this.active,
      targets: this.targets.content ?? [],
    };
  }
}
